import React, { useState, useEffect, useRef } from "react";
import { TrendingUp, Users, Home, Heart, Globe } from "lucide-react";

const ImpactSection = ({ isVisible }) => {
  const [counts, setCounts] = useState({
    families: 0,
    meals: 0,
    veterans: 0,
    communities: 0,
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  const impactStats = [
    {
      key: "families",
      value: 5000,
      suffix: "+",
      label: "Families Supported",
      icon: Users,
      description: "Received housing assistance and resources",
    },
    {
      key: "meals",
      value: 100000,
      suffix: "+",
      label: "Meals Provided",
      icon: Heart,
      description: "Through our food assistance programs",
    },
    {
      key: "veterans",
      value: 2500,
      suffix: "+",
      label: "Veterans Served",
      icon: Home,
      description: "With dedicated care and support",
    },
    {
      key: "communities",
      value: 150,
      suffix: "+",
      label: "Communities Reached",
      icon: Globe,
      description: "Across the United States",
    },
  ];

  useEffect(() => {
    if (isVisible && !hasAnimated) {
      setHasAnimated(true);
      
      const duration = 2000;
      const steps = 60;
      const interval = duration / steps;

      impactStats.forEach((stat) => {
        let current = 0;
        const increment = stat.value / steps;
        
        const timer = setInterval(() => {
          current += increment;
          if (current >= stat.value) {
            current = stat.value;
            clearInterval(timer);
          }
          setCounts((prev) => ({
            ...prev,
            [stat.key]: Math.floor(current),
          }));
        }, interval);
      });
    }
  }, [isVisible, hasAnimated]);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(0) + "K";
    }
    return num.toLocaleString();
  };

  return (
    <section
      id="impact"
      ref={sectionRef}
      className={`section-fade py-24 sm:py-32 bg-[#F8FAFC] ${isVisible ? "visible" : ""}`}
      data-testid="impact-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 sm:mb-20">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/70 mb-6">
            <span className="w-8 h-px bg-[#D4AF37]" />
            Our Impact
            <span className="w-8 h-px bg-[#D4AF37]" />
          </span>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#0A192F] tracking-tight font-['Outfit'] mb-6">
            Collective Impact,{" "}
            <span className="text-[#D4AF37]">Real Results</span>
          </h2>
          <p className="text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
            Together, our family of brands has touched countless lives and continues 
            to grow our positive impact every day.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {impactStats.map((stat, index) => (
            <div
              key={stat.key}
              className={`relative p-8 rounded-2xl bg-white border border-slate-100 hover:border-[#D4AF37]/30 transition-all duration-300 hover:shadow-xl animate-fade-in-up opacity-0 stagger-${index + 1}`}
              data-testid={`impact-stat-${stat.key}`}
            >
              {/* Gold accent line */}
              <div className="absolute top-0 left-8 right-8 h-1 bg-[#D4AF37] rounded-b-full" />
              
              {/* Icon */}
              <div className="w-14 h-14 bg-[#0A192F] rounded-xl flex items-center justify-center mb-6">
                <stat.icon className="w-6 h-6 text-white" />
              </div>

              {/* Number */}
              <div className="mb-4">
                <span className="impact-number text-[#0A192F] font-['Outfit']">
                  {formatNumber(counts[stat.key])}
                </span>
                <span className="text-3xl font-light text-[#D4AF37]">{stat.suffix}</span>
              </div>

              {/* Label */}
              <h3 className="text-xl font-semibold text-[#0A192F] font-['Outfit'] mb-2">
                {stat.label}
              </h3>
              <p className="text-sm text-[#475569]">
                {stat.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-white rounded-full shadow-sm border border-slate-100">
            <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#0A192F] font-medium">
              Growing our impact every day
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactSection;
